// @flow
import {
  colorBaseNeutral200,
  colorBrandPrimaryDefault,
  colorBrandSecondaryDefault,
  colorBrandSecondarySoftest,
} from '@duxton-web/design-tokens/themes/light';
import {
  AiOutlined,
  CopyOutlined,
  MoreVerticalUniversal,
  TickMarkOutlined,
} from '@duxton-web/icons';
import { Col, Dropdown, Flex, Table, Tooltip } from '@duxton-web/ui';
import _ from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { Link } from 'react-router';

import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import UCDStatusLabel from 'omega-rtc/components/form/ucd/common/UCDStatusLabel';
import {
  CAMPAIGN_TYPES,
  CHANNEL_DISPLAY_NAMES,
  CHANNEL_GROUP_DISPLAY_NAMES,
  CHANNEL_GROUP_ICONS_MAP,
  QUERY_PARAM_KEYS,
  TRIDENT_CAMPAIGN_TEMPLATE,
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import './UCDCampaignTable.scss';
import { handleCampaignDuplicate } from 'omega-rtc/utils/campaign-utils';
import CampaignDuplicateConfirmationPopup from 'omega-rtc/components/form/ucd/CampaignDuplicateConfirmationPopup';

/**
 * UCD Campaign Table Component
 * @param {Object} props - Component props
 * @returns {JSX.Element}
 */
const UCDCampaignTable = (props) => {
  const [duplicatePopupState, setDuplicatePopupState] = useState({
    visible: false,
    projectId: '',
    campaignId: '',
    templateType: '',
  });

  /**
   * Handles campaign duplication
   * @param {string} projectID - Project ID
   * @param {string} campaignID - Campaign ID
   * @param {string} templateType - Template type
   */
  const onDuplicate = (projectID, campaignID, templateType) => {
    if (templateType === TRIDENT_CAMPAIGN_TEMPLATE.smartSchedule) {
      setDuplicatePopupState({
        visible: true,
        projectId: projectID,
        campaignId: campaignID,
        templateType,
      });
    } else {
      handleCampaignDuplicate({
        projectId: projectID,
        campaignId: campaignID,
        templateType,
      });
    }
  };

  const handleCloseDuplicatePopup = () => {
    setDuplicatePopupState({
      ...duplicatePopupState,
      visible: false,
    });
  };

  const columnConfigs = [
    {
      dataIndex: 'campaignID',
      key: 'campaignID',
      title: 'Campaign',
      width: '25%',
      render: (value, row) => {
        const channelGroup = row.campaign.channelGroup;
        const templateType = row.campaign.templateType;
        let campaignRoute = UCD_ROUTES_LINK.CAMPAIGN_VIEW;
        if (templateType === CAMPAIGN_TYPES.journey) {
          campaignRoute = UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_VIEW;
        } else if (templateType === TRIDENT_CAMPAIGN_TEMPLATE.smartSchedule) {
          campaignRoute = UCD_ROUTES_LINK.SMART_CAMPAIGN_VIEW;
        }
        const campaignDetailPageURL = campaignRoute(row.projectID, row.campaignID) +
          (templateType === CAMPAIGN_TYPES.journey
            ? ''
            : `?${QUERY_PARAM_KEYS.previousPageIsListing}=true`);
        return (
          <Flex vertical={false} gap={4} align="middle">
            <Tooltip
              title={CHANNEL_GROUP_DISPLAY_NAMES[channelGroup]}
              placement="bottom"
              inverted
            >
              {row.campaign.channelGroup === 'trident' &&
              row.campaign.templateType ===
                TRIDENT_CAMPAIGN_TEMPLATE.smartSchedule ? (
                  <AiOutlined
                    style={{
                      backgroundColor: colorBrandSecondarySoftest,
                      color: colorBrandSecondaryDefault,
                      width: 36,
                      height: 36,
                      fontSize: 18,
                      borderRadius: 8,
                      justifyContent: 'center',
                      marginRight: '12px',
                    }}
                  />
                ) : (
                  <span>
                    <img
                      src={_.get(CHANNEL_GROUP_ICONS_MAP, channelGroup)}
                      style={{
                        float: 'left',
                        marginRight: '12px',
                        width: '36px',
                        height: '36px',
                      }}
                      alt={channelGroup}
                    />
                  </span>
                )}
            </Tooltip>
            <Col width={8} flex={1} style={{ maxWidth: '24vw' }}>
              <Link to={campaignDetailPageURL}>
                <p style={{ marginBottom: '4px', color: '#1A1A1A' }}>
                  {row.campaign.name}
                </p>
              </Link>
              <p
                style={{
                  color: '#A3A3A3',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  marginTop: '-5px',
                }}
              >
                {row.campaign.code}
              </p>
            </Col>

            <ShowIfAuthorized
              conditions={[
                {
                  resource: UCD_RESOURCE_NAMES.campaign,
                  action: UCD_ACTION_TYPES.create,
                },
              ]}
            >
              <Dropdown
                menu={{
                  items: [
                    {
                      label: 'Duplicate',
                      icon: <CopyOutlined />,
                      key: 'duplicate',
                    },
                  ],
                  onClick: ({ key }) => {
                    switch (key) {
                      case 'duplicate':
                        onDuplicate(
                          row.projectID,
                          row.campaignID,
                          row.campaign.templateType,
                        );
                        break;
                      default:
                        break;
                    }
                  },
                }}
                trigger={['click']}
              >
                <MoreVerticalUniversal style={{ fontSize: 16 }} />
              </Dropdown>
            </ShowIfAuthorized>
          </Flex>
        );
      },
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: 'Status',
      render: (value, row) => {
        return (
          <UCDStatusLabel
            status={value}
            start={row.campaign.start}
            end={row.campaign.end}
          />
        );
      },
    },
    {
      dataIndex: 'isMetricsAvailable',
      key: 'isMetricsAvailable',
      title: '',
      render: (value, row) => {
        return (
          <TickMarkOutlined
            style={{
              fontSize: 16,
              color: row.isMetricsAvailable
                ? colorBrandPrimaryDefault
                : colorBaseNeutral200,
            }}
          />
        );
      },
    },
    {
      dataIndex: ['campaign', 'verticals'],
      key: 'verticals',
      title: 'Vertical',
      // for now we only allow one vertical per campaign
      render: (value) => {
        return <span>{_.startCase(_.get(value, 0, ''))}</span>;
      },
    },
    {
      dataIndex: ['campaign', 'channels'],
      key: 'channels',
      title: 'Channel',
      render: (value) => {
        return _.map(value, (v) => <p key={v}>{CHANNEL_DISPLAY_NAMES[v]}</p>);
      },
    },
    {
      key: 'startEnd',
      title: 'Start - End Date',
      render: (value, row) => {
        return (
          <span>
            {moment(row.campaign.start).format('MM/DD/YYYY')} -{' '}
            {moment(row.campaign.end).format('MM/DD/YYYY')}
          </span>
        );
      },
    },
    {
      dataIndex: 'objective',
      title: 'Goal',
      render: (value) => {
        return <span>{_.startCase(value)}</span>;
      },
    },
  ];

  return (
    <div>
      <Table
        rowKey="campaignID"
        columns={columnConfigs}
        dataSource={_.get(props, 'campaigns', [])}
        loading={false}
        style={{
          borderRadius: '0',
        }}
        pagination={false}
      />

      <CampaignDuplicateConfirmationPopup
        visible={duplicatePopupState.visible}
        onClose={handleCloseDuplicatePopup}
        projectId={duplicatePopupState.projectId}
        campaignId={duplicatePopupState.campaignId}
        templateType={duplicatePopupState.templateType}
      />
    </div>
  );
};

export default UCDCampaignTable;
